import { z } from "zod/v4";

// BIS (Beachvolleyball Intelligence System) response schemas — mirror the Rust
// `onsand-bis` DTOs (camelCase). Option<T> serializes as a present, nullable field.

export const youthClassSchema = z.enum([
	"U16",
	"U17",
	"U18",
	"U19",
	"U20",
	"U21",
]);
export type YouthClass = z.infer<typeof youthClassSchema>;

export const classEligibilitySchema = z.object({
	ageClass: youthClassSchema,
	eligible: z.boolean(),
});
export type ClassEligibility = z.infer<typeof classEligibilitySchema>;

export const eligibilityResponseSchema = z.object({
	birthYear: z.number(),
	season: z.number(),
	classes: z.array(classEligibilitySchema),
});
export type EligibilityResponse = z.infer<typeof eligibilityResponseSchema>;

export const athleteEligibilityResponseSchema = z.object({
	athleteId: z.string(),
	name: z.string(),
	birthYear: z.number().nullable(),
	season: z.number(),
	classes: z.array(classEligibilitySchema),
});
export type AthleteEligibilityResponse = z.infer<
	typeof athleteEligibilityResponseSchema
>;

export const tournamentBriefSchema = z.object({
	id: z.string(),
	name: z.string(),
	federation: z.string(),
	ageCategory: z.string().nullable(),
	startDate: z.string(),
	endDate: z.string(),
});
export type TournamentBrief = z.infer<typeof tournamentBriefSchema>;

export const scheduleCollisionSchema = z.object({
	a: tournamentBriefSchema,
	b: tournamentBriefSchema,
});
export type ScheduleCollision = z.infer<typeof scheduleCollisionSchema>;

export const doubleRegistrationSchema = z.object({
	player: z.string(),
	teams: z.array(z.string()),
});
export type DoubleRegistration = z.infer<typeof doubleRegistrationSchema>;

export const championEntrySchema = z.object({
	federation: z.string(),
	ageCategory: z.string().nullable(),
	gender: z.string().nullable(),
	tournament: z.object({
		id: z.string(),
		name: z.string(),
		startDate: z.string(),
	}),
	champion: z.object({
		team: z.string(),
		player1: z.string().nullable(),
		player2: z.string().nullable(),
		club: z.string().nullable(),
		points: z.string(),
	}),
});
export type ChampionEntry = z.infer<typeof championEntrySchema>;

export const teamPredictionSchema = z.object({
	team: z.string(),
	rank: z.number(),
	points: z.number().nullable(),
	withinCutoff: z.boolean(),
	admissionProbability: z.number(),
	waitlistPosition: z.number().nullable(),
	waitlistProbability: z.number().nullable(),
});
export type TeamPrediction = z.infer<typeof teamPredictionSchema>;

export const tournamentPredictionSchema = z.object({
	tournamentId: z.string(),
	maxTeams: z.number().nullable(),
	directSlots: z.number(),
	teamCount: z.number(),
	teams: z.array(teamPredictionSchema),
});
export type TournamentPrediction = z.infer<typeof tournamentPredictionSchema>;

export const snapshotPointSchema = z.object({
	capturedOn: z.string(),
	points: z.number().nullable(),
	rank: z.number().nullable(),
});
export type SnapshotPoint = z.infer<typeof snapshotPointSchema>;

export const listSeriesSchema = z.object({
	list: z.string(),
	points: z.array(snapshotPointSchema),
});
export type ListSeries = z.infer<typeof listSeriesSchema>;

export const snapshotsResponseSchema = z.object({
	athleteId: z.string(),
	series: z.array(listSeriesSchema),
});
export type SnapshotsResponse = z.infer<typeof snapshotsResponseSchema>;

export const athleteHitSchema = z.object({
	id: z.string(),
	name: z.string(),
	club: z.string(),
	federation: z.string(),
	gender: z.string(),
	ageCategory: z.string(),
	imageUrl: z.string().nullable(),
	bestRank: z.number(),
	bestPoints: z.string(),
	bestLvPoints: z.number().default(0),
	bestDvvPoints: z.number().default(0),
});
export type AthleteHit = z.infer<typeof athleteHitSchema>;

// Squad planning schemas

export const playerSchema = z.object({
	id: z.string(),
	name: z.string(),
	birthYear: z.number().nullable(),
	federation: z.string().nullable(),
	club: z.string().nullable(),
	dvvLicense: z.enum(["yes", "no", "adult"]),
	youthLicense: z.enum(["yes", "no", "adult"]),
	refereeLicense: z.string().nullable(),
	samsCode: z.string().nullable(),
	lvPoints: z.number(),
	dvvPoints: z.number(),
	heightCm: z.number().nullable(),
	position: z.string().nullable(),
	notes: z.string().nullable(),
	active: z.boolean(),
	dvvId: z.string().nullable(),
	dvvSyncedAt: z.string().nullable(),
});
export type Player = z.infer<typeof playerSchema>;

export const seasonPlanItemSchema = z.object({
	id: z.string(),
	month: z.string().nullable(),
	dateLabel: z.string().nullable(),
	startDate: z.string().nullable(),
	endDate: z.string().nullable(),
	eventName: z.string(),
	location: z.string().nullable(),
	kind: z.string().nullable(),
	recommendation: z.string().nullable(),
	teamFocus: z.string().nullable(),
	sortOrder: z.number(),
});
export type SeasonPlanItem = z.infer<typeof seasonPlanItemSchema>;

export const bisTournamentSchema = z.object({
	id: z.string(),
	name: z.string(),
	federation: z.string(),
	category: z.string().nullable(),
	ageClass: z.string().nullable(),
	gender: z.string(),
	startDate: z.string().nullable(),
	endDate: z.string().nullable(),
	deadline: z.string().nullable(),
	location: z.string().nullable(),
	postalCode: z.string().nullable(),
	capacityFilled: z.number().nullable(),
	capacityTotal: z.number().nullable(),
	sourceSheet: z.string(),
	colIndex: z.number(),
	notes: z.string().nullable(),
});
export type BisTournament = z.infer<typeof bisTournamentSchema>;

export const playerRefSchema = z.object({
	id: z.string(),
	name: z.string(),
});
export type PlayerRef = z.infer<typeof playerRefSchema>;

export const entryStatusSchema = z.enum([
	"entered",
	"planned",
	"break",
	"vacation",
	"substitute",
	"injured",
	"declined",
	"note",
]);
export type EntryStatus = z.infer<typeof entryStatusSchema>;

export const teamEntrySchema = z.object({
	id: z.string(),
	tournamentId: z.string(),
	player1: playerRefSchema.nullable(),
	player2: playerRefSchema.nullable(),
	status: entryStatusSchema,
	rawLabel: z.string().nullable(),
	note: z.string().nullable(),
});
export type TeamEntry = z.infer<typeof teamEntrySchema>;

export const campSchema = z.object({
	id: z.string(),
	name: z.string(),
	startDate: z.string().nullable(),
	endDate: z.string().nullable(),
	location: z.string().nullable(),
	limited: z.boolean(),
	notes: z.string().nullable(),
	sortOrder: z.number(),
});
export type Camp = z.infer<typeof campSchema>;

export const campSignupSchema = z.object({
	id: z.string(),
	campId: z.string(),
	player: playerRefSchema.nullable(),
	personName: z.string().nullable(),
	role: z.enum(["player", "coach", "helper"]),
	status: z.string().nullable(),
	carpool: z.string().nullable(),
	accommodation: z.string().nullable(),
	food: z.string().nullable(),
	wave: z.string().nullable(),
	note: z.string().nullable(),
});
export type CampSignup = z.infer<typeof campSignupSchema>;

export const refereeCourseSchema = z.object({
	id: z.string(),
	region: z.string().nullable(),
	kind: z.enum(["ausbildung", "fortbildung_c", "fortbildung_cb"]),
	format: z.string().nullable(),
	location: z.string().nullable(),
	startDate: z.string().nullable(),
	endDate: z.string().nullable(),
	registrationFrom: z.string().nullable(),
	registrationTo: z.string().nullable(),
	online: z.boolean(),
	sortOrder: z.number(),
});
export type RefereeCourse = z.infer<typeof refereeCourseSchema>;

export const bptFutureSchema = z.object({
	id: z.string(),
	month: z.string().nullable(),
	category: z.string(),
	name: z.string(),
	country: z.string().nullable(),
	startDate: z.string().nullable(),
	endDate: z.string().nullable(),
	dvvStatus: z.string().nullable(),
	entryDeadline: z.string().nullable(),
	assignment: z.string().nullable(),
	sortOrder: z.number(),
});
export type BptFuture = z.infer<typeof bptFutureSchema>;

export const ageClassSchema = z.object({
	id: z.string(),
	ageClass: z.string(),
	cutoff2026: z.string().nullable(),
	cutoff2027: z.string().nullable(),
	cutoff2028: z.string().nullable(),
	cutoff2029: z.string().nullable(),
	netHeight: z.string().nullable(),
	fieldSize: z.string().nullable(),
	teamSize: z.string().nullable(),
	refereeLicense: z.string().nullable(),
	sortOrder: z.number(),
});
export type AgeClass = z.infer<typeof ageClassSchema>;

export const loadResponseSchema = z.object({
	season: z.number(),
	months: z.array(z.number()),
	players: z.array(
		z.object({
			playerId: z.string(),
			name: z.string(),
			federation: z.string().nullable(),
			byMonth: z.array(z.object({ month: z.number(), count: z.number() })),
			total: z.number(),
		}),
	),
});
export type LoadResponse = z.infer<typeof loadResponseSchema>;

export const tournamentHitSchema = z.object({
	id: z.string(),
	name: z.string(),
	federation: z.string(),
	ageCategory: z.string().nullable(),
	startDate: z.string(),
});
export type TournamentHit = z.infer<typeof tournamentHitSchema>;

// DVV link picker (searches the discovery-owned `scraped_athletes` table).
// Slim projection: enough for the picker UI + a sync decision, not the full
// scraped-athlete document (that lives in `schemas/athlete`).
export const dvvPickerAthleteSchema = z.object({
	id: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	club: z.string().nullable(),
	federation: z.string(),
	licenseNumber: z.string().nullable(),
	bestDvvPoints: z.number().nullable(),
	bestLvPoints: z.number().nullable(),
});
export type DvvPickerAthlete = z.infer<typeof dvvPickerAthleteSchema>;

// Report of a bulk DVV sync: each `updated` row had its `dvv_license`,
// `dvv_points`, `lv_points` columns overwritten from the linked scraped
// athlete; each `skipped` row had no link (or the linked athlete is gone).
export const dvvSyncOutcomeSchema = z.object({
	playerId: z.string(),
	name: z.string(),
	dvvLicense: z.enum(["yes", "no", "adult"]),
	dvvPoints: z.number(),
	lvPoints: z.number(),
});
export const dvvSyncReportSchema = z.object({
	updated: z.array(dvvSyncOutcomeSchema),
	skipped: z.array(z.string()),
});
export type DvvSyncOutcome = z.infer<typeof dvvSyncOutcomeSchema>;
export type DvvSyncReport = z.infer<typeof dvvSyncReportSchema>;

// ---------------------------------------------------------------------------
// BI-shell record lists (Phase 0b) — the 12 user-listed categories.
// All `Option<T>` in Rust serialize as present, nullable fields in TS.
// ---------------------------------------------------------------------------

// 1. Rankings — one row per (athlete, list, season) point entry.
export const rankingRowSchema = z.object({
	athleteId: z.string(),
	athleteName: z.string(),
	club: z.string().nullable(),
	federation: z.string(),
	gender: z.string().nullable(),
	ageGroup: z.string().nullable(),
	rankingType: z.string(),
	listName: z.string().nullable(),
	season: z.number().nullable(),
	rank: z.number().nullable(),
	points: z.number().nullable(),
	trend: z.string().nullable(),
	partner: z.string().nullable(),
	validFrom: z.string().nullable(),
	sourceUrl: z.string().nullable(),
});
export type RankingRow = z.infer<typeof rankingRowSchema>;

// 2. Tournament entries — every registered team across scraped tournaments.
export const tournamentEntryRowSchema = z.object({
	teamId: z.string(),
	tournamentId: z.string(),
	tournamentName: z.string(),
	teamName: z.string(),
	player1Name: z.string().nullable(),
	player2Name: z.string().nullable(),
	club1: z.string().nullable(),
	club2: z.string().nullable(),
	seed: z.number().nullable(),
	rankingPoints: z.string().nullable(),
	status: z.string().nullable(),
	registeredAt: z.string().nullable(),
});
export type TournamentEntryRow = z.infer<typeof tournamentEntryRowSchema>;

// 3. Entry lists — ordered admission / waitlist view for one tournament.
export const entryListRowSchema = z.object({
	teamId: z.string(),
	tournamentId: z.string(),
	teamName: z.string(),
	player1Name: z.string().nullable(),
	player2Name: z.string().nullable(),
	listType: z.string(),
	position: z.number(),
	rankingPoints: z.string().nullable(),
	status: z.string().nullable(),
	cutoffPoints: z.number().nullable(),
});
export type EntryListRow = z.infer<typeof entryListRowSchema>;

// 4. Tournament results — flatten `scraped_tournaments.results` JSONB.
export const resultRowSchema = z.object({
	tournamentId: z.string(),
	tournamentName: z.string(),
	tournamentDate: z.string().nullable(),
	placement: z.number(),
	teamLabel: z.string(),
	partner: z.string().nullable(),
	category: z.string().nullable(),
	points: z.string().nullable(),
	athleteId: z.string().nullable(),
});
export type ResultRow = z.infer<typeof resultRowSchema>;

// 5. Tournament matches — bracket / round / score rows.
export const matchRowSchema = z.object({
	id: z.string(),
	tournamentId: z.string(),
	tournamentName: z.string(),
	matchNumber: z.number().nullable(),
	roundName: z.string().nullable(),
	roundLevel: z.number().nullable(),
	team1Name: z.string().nullable(),
	team2Name: z.string().nullable(),
	score: z.string().nullable(),
	setScores: z.array(z.unknown()),
	court: z.string().nullable(),
	scheduledAt: z.string().nullable(),
	status: z.string(),
});
export type MatchRow = z.infer<typeof matchRowSchema>;

// 6. DBM qualification slots — derived from champions extraction.
export const dbmSlotRowSchema = z.object({
	season: z.number(),
	ageCategory: z.string().nullable(),
	gender: z.string().nullable(),
	federation: z.string(),
	tournamentId: z.string(),
	tournamentName: z.string(),
	championTeam: z.string().nullable(),
	championClub: z.string().nullable(),
	qualifiesForDbm: z.boolean(),
	sourceUrl: z.string().nullable(),
});
export type DbmSlotRow = z.infer<typeof dbmSlotRowSchema>;

// 7. International tour stops (BPT, CEV, FIVB).
export const internationalRowSchema = z.object({
	id: z.string(),
	name: z.string(),
	source: z.string(),
	sourceUrl: z.string().nullable(),
	tour: z.string(),
	gender: z.string().nullable(),
	startDate: z.string().nullable(),
	endDate: z.string().nullable(),
	location: z.string().nullable(),
	venue: z.string().nullable(),
	maxTeams: z.number().nullable(),
	registeredTeams: z.number().nullable(),
	status: z.string(),
});
export type InternationalRow = z.infer<typeof internationalRowSchema>;

// 8. Generic entity snapshots — Phase 1 only covers ranking snapshots.
export const snapshotRowSchema = z.object({
	entityType: z.string(),
	entityId: z.string(),
	capturedOn: z.string(),
	list: z.string(),
	points: z.number().nullable(),
	rank: z.number().nullable(),
});
export type SnapshotRow = z.infer<typeof snapshotRowSchema>;

// Data-source freshness — one row per federation/source (DVV, the 14
// Landesverbände, the international tours). Drives the "Datenquelle" panel so a
// non-technical analyst can see when each source was last scraped.
export const sourceHealthSchema = z.object({
	federation: z.string(),
	athletes: z.number(),
	tournaments: z.number(),
	lastScrapedAt: z.string().nullable(),
});
export type SourceHealth = z.infer<typeof sourceHealthSchema>;
